import { useState } from 'react'
import { Bell, ChevronDown, Check } from 'lucide-react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { BottomSheet, Logo, ServiceIcon, SERVICE_LABEL, type ServiceActivity } from '@waago/ui'
import { request } from '@waago/api'
import { useCurrentActivity, type CurrentActivity } from '../lib/current-activity'

/**
 * The AF app header — Figma frame `4650:4369` and the other Prestataire frames
 * that carry it.
 *
 * <p>Three things on a brand-800 band: the wordmark, the "Activité actuelle"
 * pill, and the bell with its unread count. The pill is the only control that
 * needed a decision, and it is documented where the state lives
 * ({@link useCurrentActivity}): it filters what the screens show, it does not
 * decide what dispatch offers.
 *
 * <p>A provider with one activity is not shown the pill as a control — there is
 * nothing to switch to, and a chevron that does nothing is worse than no
 * chevron. They still see which activity they are on.
 */
const LONG_LABEL: Record<ServiceActivity, string> = {
  TRANSPORT_PERSON: 'Transport de personne',
  DELIVERY: 'Livraison',
  EQUIPMENT_RENTAL: 'Location d’engins',
  DRIVER_HIRE: 'Mise à disposition de conducteur',
}

export function ActivityHeader() {
  const navigate = useNavigate()
  const { activity, setActivity, options } = useCurrentActivity()
  const [picking, setPicking] = useState(false)

  const summary = useQuery({
    queryKey: ['me', 'summary'],
    queryFn: () => request<{ unreadNotifications: number }>('/me/summary'),
    staleTime: 30_000,
  })
  const unread = summary.data?.unreadNotifications ?? 0

  const switchable = options.length > 1
  const label = activity ? LONG_LABEL[activity] : 'Toutes les activités'

  const pill = (
    <>
      {/* The frame draws the current activity's own glyph, not a generic icon:
          it is what a provider recognises at a glance. */}
      <span className="text-slate-500">
        {activity ? (
          <ServiceIcon activity={activity} className="size-9" />
        ) : (
          <ServiceIcon activity={options[0] ?? 'TRANSPORT_PERSON'} className="size-9 opacity-50" />
        )}
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-1">
          {/* slate-500 rather than the frame's #94a3b8: that colour on the
              pill's slate-50 measures 2.51:1, well under the 4.5:1 the gate
              requires, and this is the line that says what the control is. */}
          <span className="text-xs text-slate-500">Activité actuelle</span>
          {switchable ? <ChevronDown className="size-4 text-slate-500" aria-hidden="true" /> : null}
        </span>
        <span className="mt-1 block truncate text-sm font-semibold text-slate-600">{label}</span>
      </span>
    </>
  )

  return (
    <header className="bg-brand-800 shrink-0 px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-5">
      <Logo tone="white" className="h-7 w-[85px]" />

      <div className="mt-3 flex items-center gap-3">
        {switchable ? (
          <button
            type="button"
            onClick={() => setPicking(true)}
            aria-haspopup="dialog"
            aria-label={`Activité actuelle : ${label}. Changer d’activité`}
            className="flex flex-1 items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left"
          >
            {pill}
          </button>
        ) : (
          <div className="flex flex-1 items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            {pill}
          </div>
        )}

        <button
          type="button"
          onClick={() => void navigate('/notifications')}
          aria-label={
            unread > 0 ? `Notifications, ${unread} non lues` : 'Notifications, aucune non lue'
          }
          className="relative flex size-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50"
        >
          <Bell className="size-5 text-slate-600" aria-hidden="true" />
          {/* An unlabelled dot, not a count. The frame draws the number, but
              white on #ff343d measures 3.61:1 and 8px is unreadable anyway —
              the same trade the tab bar already makes. The count is not lost:
              it is in this button's accessible name, where a screen reader
              actually reads it and a red circle says nothing. */}
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 size-3 rounded-full bg-[#ff343d] ring-2 ring-slate-50"
            />
          ) : null}
        </button>
      </div>

      <BottomSheet
        open={picking}
        onClose={() => setPicking(false)}
        title="Activité actuelle"
        subtitle="Ce choix filtre vos écrans. Vous continuez à recevoir toutes vos demandes."
      >
        <ul className="flex flex-col gap-2 p-4">
          {([null, ...options] as CurrentActivity[]).map((option) => {
            const selected = option === activity
            return (
              <li key={option ?? 'all'}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setActivity(option)
                    setPicking(false)
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                    selected ? 'border-brand-600 bg-brand-50' : 'border-slate-200 bg-white'
                  }`}
                >
                  {option ? (
                    <ServiceIcon
                      activity={option}
                      className={`size-7 ${selected ? 'text-brand-600' : 'text-slate-500'}`}
                    />
                  ) : (
                    <span className="size-7" aria-hidden="true" />
                  )}
                  <span className="flex-1 text-sm font-medium text-slate-800">
                    {option ? LONG_LABEL[option] : 'Toutes les activités'}
                  </span>
                  {selected ? <Check className="text-brand-600 size-5" aria-hidden="true" /> : null}
                </button>
              </li>
            )
          })}
        </ul>
      </BottomSheet>
    </header>
  )
}

/** The short label the screens print beside a filtered list. */
export function activityLabel(activity: ServiceActivity): string {
  return SERVICE_LABEL[activity]
}
